import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Send, Database, BarChart3, MapPin, Zap, Clock, ArrowRight, RotateCcw, Download, Lock } from 'lucide-react';
import { useEntitlements } from '../contexts/EntitlementsContext';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';

const SUGGESTION_GROUPS = [
  {
    icon: Database, label: 'Data',
    items: [
      "Break down the 37,769 businesses by category: trading invisible, shell, dissolved, and active",
      "How many businesses in each sector have zero digital presence?",
    ]
  },
  {
    icon: MapPin, label: 'Districts',
    items: [
      "Compare TS18 and TS19 — which has worse digital exclusion and why?",
      "Which postcode district has the highest concentration of shell companies?",
    ]
  },
  {
    icon: BarChart3, label: 'Insights',
    items: [
      "Explain the correlation between IMD deprivation and digital adoption",
      "What is the ROI case for digital intervention across the Tees Valley?",
    ]
  },
  {
    icon: Zap, label: 'Sectors',
    items: [
      "Why does Accommodation & Food have 27% adoption despite a 3.75 average score?",
      "Which sectors have zero customer reviews across all businesses?",
    ]
  },
];

function MarkdownRenderer({ text }) {
  if (!text) return null;

  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="font-display text-sm font-bold text-ink mt-4 mb-1.5">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="font-display text-base font-extrabold text-ink mt-5 mb-2">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="font-display text-lg font-extrabold text-ink mt-5 mb-2">{line.slice(2)}</h1>);
    } else if (line.startsWith('- **') || line.startsWith('* **')) {
      const content = line.slice(2);
      elements.push(
        <div key={i} className="flex gap-2 py-0.5 pl-1">
          <span className="text-brand-orange mt-1 text-[10px]">&#9654;</span>
          <span className="font-body text-[13px] text-ink/80 leading-relaxed" dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content.replace(/\*\*(.*?)\*\*/g, '<strong class="text-ink font-semibold">$1</strong>'))
          }} />
        </div>
      );
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <div key={i} className="flex gap-2 py-0.5 pl-1">
          <span className="text-ink/20 mt-0.5">&#8226;</span>
          <span className="font-body text-[13px] text-ink/70 leading-relaxed" dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-ink font-semibold">$1</strong>'))
          }} />
        </div>
      );
    } else if (line.match(/^\d+\.\s/)) {
      const num = line.match(/^(\d+)\./)[1];
      const content = line.replace(/^\d+\.\s*/, '');
      elements.push(
        <div key={i} className="flex gap-2.5 py-0.5 pl-1">
          <span className="font-display text-[11px] font-bold text-brand-orange min-w-[16px]">{num}.</span>
          <span className="font-body text-[13px] text-ink/80 leading-relaxed" dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content.replace(/\*\*(.*?)\*\*/g, '<strong class="text-ink font-semibold">$1</strong>'))
          }} />
        </div>
      );
    } else if (line.startsWith('|') && line.includes('|')) {
      // Table row
      const cells = line.split('|').filter(c => c.trim()).map(c => c.trim());
      const isSeparator = cells.every(c => c.match(/^[-:]+$/));
      if (!isSeparator) {
        const isHeader = i + 1 < lines.length && lines[i + 1]?.includes('---');
        elements.push(
          <div key={i} className={`grid gap-1 py-1 px-1 text-[11px] font-body ${isHeader ? 'font-semibold text-ink border-b border-ink/10' : 'text-ink/60'}`}
               style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
            {cells.map((cell, ci) => <span key={ci} dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(cell.replace(/\*\*(.*?)\*\*/g, '<strong class="text-ink">$1</strong>'))
            }} />)}
          </div>
        );
      }
    } else if (line.startsWith('```')) {
      // Code block
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <pre key={`code-${i}`} className="bg-ink/[0.03] border border-ink/5 rounded p-3 my-2 overflow-x-auto">
          <code className="font-body text-[11px] text-ink/70">{codeLines.join('\n')}</code>
        </pre>
      );
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} className="border-l-2 border-brand-orange/30 pl-3 py-1 my-2">
          <span className="font-body text-[13px] text-ink/50 italic">{line.slice(2)}</span>
        </blockquote>
      );
    } else if (line.trim() === '') {
      elements.push(<div key={i} className="h-2" />);
    } else {
      elements.push(
        <p key={i} className="font-body text-[13px] text-ink/70 leading-relaxed py-0.5" dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(line
            .replace(/\*\*(.*?)\*\*/g, '<strong class="text-ink font-semibold">$1</strong>')
            .replace(/`(.*?)`/g, '<code class="bg-ink/[0.04] px-1 py-0.5 rounded text-[11px] font-medium text-brand-orange">$1</code>'))
        }} />
      );
    }
    i++;
  }
  return <div className="space-y-0">{elements}</div>;
}

export default function ChatPage() {
  const { has } = useEntitlements();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]') || scrollRef.current;
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, loading, scrollToBottom]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text, ts: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const { data } = await api.post('/chat', { message: text, session_id: sessionId });
      if (data.session_id) setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, ts: Date.now() }]);
    } catch (e) {
      const detail = e.response?.data?.detail;
      // 402 upgrade-required → show inline upgrade prompt
      if (e.response?.status === 402 && typeof detail === 'object') {
        setMessages(prev => [...prev, {
          role: 'upgrade',
          error: detail.error,
          minPlan: detail.min_plan,
          message: detail.message,
          paygUrl: detail.payg_url,
          upgradeUrl: detail.upgrade_url,
          ts: Date.now(),
        }]);
      } else {
        const errMsg = (typeof detail === 'string' ? detail : detail?.message) || 'Query failed. Please try again.';
        setMessages(prev => [...prev, { role: 'error', content: String(errMsg), ts: Date.now() }]);
      }
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const newSession = () => {
    setMessages([]);
    setSessionId('');
    inputRef.current?.focus();
  };

  const exportChatPDF = async () => {
    if (messages.length === 0) return;
    if (!has('ai_response_download')) {
      toast.error('Exporting AI responses requires Pro plan or higher.', {
        action: { label: 'Upgrade', onClick: () => window.location.href = '/pricing' },
      });
      return;
    }
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const W = 210, M = 15, CW = W - 2 * M;

      // Header
      doc.setFillColor(26, 24, 20); doc.rect(0, 0, W, 28, 'F');
      doc.setFillColor(201, 79, 26); doc.rect(M, 6, 7, 7, 'F');
      doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
      doc.text('S', M + 2.5, 11);
      doc.setFontSize(10); doc.text('Intelligence Query Summary', M + 11, 11);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(160, 160, 160);
      doc.text(`Adoptic · Tees Valley Digital Register | ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} | CONFIDENTIAL`, M, 22);

      let y = 36;

      // Session info
      doc.setFontSize(5); doc.setTextColor(140, 140, 140);
      doc.text(`Session: ${sessionId || 'N/A'} | Messages: ${messages.length} | Exported: ${new Date().toLocaleTimeString('en-GB')}`, M, y);
      y += 6;

      // Messages
      messages.forEach((msg) => {
        if (y > 270) { doc.addPage(); y = 15; }

        if (msg.role === 'user') {
          doc.setFillColor(26, 24, 20); doc.roundedRect(M, y, CW, 0, 1, 1, 'F');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(5); doc.setTextColor(201, 79, 26);
          doc.text('YOU', M + 2, y + 3.5);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(26, 24, 20);
          const uLines = doc.splitTextToSize(msg.content, CW - 4);
          const uH = uLines.length * 3 + 5;
          doc.setFillColor(245, 244, 240); doc.roundedRect(M, y, CW, uH, 1, 1, 'F');
          doc.setDrawColor(201, 79, 26); doc.setLineWidth(0.5); doc.line(M, y, M, y + uH); doc.setLineWidth(0.1);
          doc.setFont('helvetica', 'bold'); doc.setFontSize(5); doc.setTextColor(201, 79, 26);
          doc.text('QUERY', M + 2, y + 3.5);
          doc.setFont('helvetica', 'normal'); doc.setFontSize(6); doc.setTextColor(26, 24, 20);
          doc.text(uLines, M + 2, y + 7);
          y += uH + 3;
        } else if (msg.role === 'assistant') {
          if (y > 270) { doc.addPage(); y = 15; }

          // Parse markdown-like content for PDF
          const lines = msg.content.split('\n');
          doc.setFont('helvetica', 'bold'); doc.setFontSize(5); doc.setTextColor(45, 110, 78);
          doc.text('INTELLIGENCE RESPONSE', M, y); y += 4;

          lines.forEach(line => {
            if (y > 280) { doc.addPage(); y = 15; }

            const clean = line.replace(/\*\*/g, '').replace(/`/g, '');
            if (line.startsWith('## ') || line.startsWith('# ')) {
              doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(26, 24, 20);
              doc.text(clean.replace(/^#+\s*/, ''), M, y); y += 4;
            } else if (line.startsWith('### ')) {
              doc.setFont('helvetica', 'bold'); doc.setFontSize(6); doc.setTextColor(26, 24, 20);
              doc.text(clean.replace(/^#+\s*/, ''), M, y); y += 3.5;
            } else if (line.startsWith('- ') || line.startsWith('* ')) {
              doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(60, 60, 60);
              const bLines = doc.splitTextToSize(clean.replace(/^[-*]\s*/, ''), CW - 6);
              doc.text('\u2022', M + 1, y);
              doc.text(bLines, M + 4, y);
              y += bLines.length * 2.6 + 1;
            } else if (line.match(/^\d+\.\s/)) {
              doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(60, 60, 60);
              const nLines = doc.splitTextToSize(clean, CW - 4);
              doc.text(nLines, M + 2, y);
              y += nLines.length * 2.6 + 1;
            } else if (line.startsWith('|')) {
              doc.setFont('helvetica', 'normal'); doc.setFontSize(4.5); doc.setTextColor(80, 80, 80);
              const tLine = clean.replace(/\|/g, '  ').trim();
              if (tLine && !tLine.match(/^[-:\s]+$/)) {
                doc.text(tLine, M + 1, y); y += 2.5;
              }
            } else if (clean.trim()) {
              doc.setFont('helvetica', 'normal'); doc.setFontSize(5.5); doc.setTextColor(60, 60, 60);
              const pLines = doc.splitTextToSize(clean, CW);
              doc.text(pLines, M, y);
              y += pLines.length * 2.6 + 0.5;
            } else {
              y += 1.5;
            }
          });
          y += 4;
          doc.setDrawColor(230, 230, 230); doc.line(M, y, M + CW, y); y += 4;
        }
      });

      // Footer on all pages
      const p = doc.getNumberOfPages();
      for (let i = 1; i <= p; i++) {
        doc.setPage(i);
        doc.setFillColor(26, 24, 20); doc.rect(0, 287, W, 10, 'F');
        doc.setTextColor(130, 130, 130); doc.setFontSize(4);
        doc.text('Adoptic · Tees Valley Digital Register | GraphRAG Intelligence Query | Nexintell Limited | CONFIDENTIAL', M, 291);
        doc.text(`Page ${i}/${p}`, W - M, 291, { align: 'right' });
      }

      doc.save(`SDR_Query_${new Date().toISOString().slice(0, 10)}_${new Date().toLocaleTimeString('en-GB').replace(/:/g, '')}.pdf`);
    } catch (e) {
      console.error('Chat PDF export error:', e);
    }
  };

  const hasMessages = messages.length > 0;

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-[#FAFAF8]" data-testid="chat-page">

      {/* Empty state */}
      {!hasMessages && (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-[720px] w-full">
            {/* Header */}
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-1.5 bg-ink/[0.03] border border-ink/5 rounded-full px-3 py-1 mb-5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#2D6E4E] animate-pulse" />
                <span className="font-body text-[10px] uppercase tracking-widest text-ink/40">AI Query — Natural-Language Intelligence</span>
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink tracking-tight leading-tight">
                Ask about Tees Valley&apos;s businesses,<br />
                <span className="text-ink/25">infrastructure, or communities.</span>
              </h1>
              <p className="font-body text-sm text-ink/50 max-w-2xl mx-auto mt-4 leading-relaxed">
                Get verified, data-grounded answers powered by the Infralytics Meaningful Connectivity Audit Platform. Combines Companies House, Ofcom, MHCLG IoD 2025, and postcode-level adoption modelling across 2.5 million data points.
              </p>
            </div>

            {/* Suggestion grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SUGGESTION_GROUPS.map((group, gi) => (
                <div key={gi} className="space-y-2">
                  <div className="flex items-center gap-1.5 px-1">
                    <group.icon className="w-3 h-3 text-ink/20" strokeWidth={1.5} />
                    <span className="font-body text-[9px] uppercase tracking-widest text-ink/25">{group.label}</span>
                  </div>
                  {group.items.map((item, ii) => (
                    <button
                      key={ii}
                      data-testid={`suggestion-${gi}-${ii}`}
                      onClick={() => sendMessage(item)}
                      className="w-full text-left bg-white border border-ink/[0.06] hover:border-ink/15 p-3.5 rounded-lg transition-all duration-200 group hover:shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
                    >
                      <span className="font-body text-[12.5px] text-ink/50 group-hover:text-ink/70 leading-relaxed line-clamp-2 transition-colors">{item}</span>
                      <ArrowRight className="w-3 h-3 text-ink/0 group-hover:text-brand-orange mt-1 transition-all duration-200 group-hover:translate-x-0.5" />
                    </button>
                  ))}
                </div>
              ))}
            </div>

            {/* Input bar — empty state */}
            <div className="mt-8 bg-white border border-ink/[0.08] rounded-xl shadow-[0_2px_20px_rgba(0,0,0,0.03)] overflow-hidden">
              <form onSubmit={e => { e.preventDefault(); sendMessage(input); }} className="flex items-center">
                <input
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ask a question — for example, 'Which sectors in TS18 have the lowest digital adoption?'"
                  className="flex-1 px-5 py-4 font-body text-sm text-ink bg-transparent outline-none placeholder:text-ink/25"
                  disabled={loading}
                />
                <button
                  data-testid="chat-send"
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="mr-2 w-9 h-9 rounded-lg bg-ink text-white flex items-center justify-center disabled:opacity-20 hover:bg-ink/80 transition-all"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>

            <p className="text-center font-body text-[10px] text-ink/20 mt-4">
              Powered by Claude Sonnet 4.5 + GraphRAG over Companies House, Ofcom, ONS IMD
            </p>
          </div>
        </div>
      )}

      {/* Conversation state */}
      {hasMessages && (
        <>
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-ink/[0.04] bg-white/60 backdrop-blur-sm">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#2D6E4E] animate-pulse" />
              <span className="font-body text-[11px] text-ink/30 uppercase tracking-wider">GraphRAG Session</span>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={exportChatPDF} className="flex items-center gap-1.5 font-body text-[11px] text-ink/30 hover:text-brand-orange transition-colors" data-testid="export-chat-pdf">
                {has('ai_response_download') ? <Download className="w-3 h-3" /> : <Lock className="w-3 h-3 text-brand-orange" />} Export PDF
                {!has('ai_response_download') && <span className="text-[9px] uppercase tracking-wider text-brand-orange/80 ml-0.5">Pro</span>}
              </button>
              <button onClick={newSession} className="flex items-center gap-1.5 font-body text-[11px] text-ink/30 hover:text-ink/60 transition-colors" data-testid="new-session">
                <RotateCcw className="w-3 h-3" /> New query
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full" ref={scrollRef}>
              <div className="max-w-[760px] mx-auto px-4 py-6 space-y-5">
                {messages.map((msg, i) => (
                  <div key={i} className="animate-fade-in" style={{ animationDelay: `${Math.min(i * 50, 200)}ms` }}>
                    {msg.role === 'user' ? (
                      <div className="flex justify-end">
                        <div className="max-w-[85%] bg-ink text-white px-4 py-3 rounded-2xl rounded-br-sm">
                          <p className="font-body text-[13px] leading-relaxed">{msg.content}</p>
                        </div>
                      </div>
                    ) : msg.role === 'error' ? (
                      <div className="bg-[#8A1A1A]/[0.04] border border-[#8A1A1A]/10 px-4 py-3 rounded-xl">
                        <p className="font-body text-[13px] text-[#8A1A1A]">{msg.content}</p>
                      </div>
                    ) : msg.role === 'upgrade' ? (
                      <div className="bg-brand-orange/[0.06] border border-brand-orange/30 px-5 py-4 rounded-xl" data-testid={`upgrade-prompt-${i}`}>
                        <p className="font-display text-sm font-extrabold text-ink mb-1">Upgrade required</p>
                        <p className="font-body text-[13px] text-ink/70 leading-relaxed mb-3">{msg.message}</p>
                        <div className="flex flex-wrap gap-2">
                          <a href="/pricing" data-testid={`upgrade-cta-${i}`} className="inline-flex items-center gap-1.5 bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-xs font-semibold px-3.5 py-2 rounded-md">
                            Upgrade to {(msg.minPlan || 'Pro').replace(/^\w/, c => c.toUpperCase())} <ArrowRight className="w-3.5 h-3.5" />
                          </a>
                          {msg.paygUrl && (
                            <a href={msg.paygUrl} className="inline-flex items-center gap-1.5 border border-ink/20 hover:bg-ink/5 text-ink font-body text-xs font-semibold px-3.5 py-2 rounded-md">
                              Buy a prompt credit
                            </a>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white border border-ink/[0.05] rounded-2xl rounded-bl-sm px-5 py-4 shadow-[0_1px_8px_rgba(0,0,0,0.02)]" data-testid={`ai-response-${i}`}>
                        <MarkdownRenderer text={msg.content} />
                        <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-ink/[0.04]">
                          <div className="w-1 h-1 rounded-full bg-[#2D6E4E]" />
                          <span className="font-body text-[9px] text-ink/20 uppercase tracking-wider">Adoptic Intelligence · Tees Valley</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="animate-fade-in">
                    <div className="bg-white border border-ink/[0.05] rounded-2xl rounded-bl-sm px-5 py-4 shadow-[0_1px_8px_rgba(0,0,0,0.02)]">
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-ink/20 animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-1.5 h-1.5 rounded-full bg-ink/20 animate-bounce" style={{ animationDelay: '120ms' }} />
                          <div className="w-1.5 h-1.5 rounded-full bg-ink/20 animate-bounce" style={{ animationDelay: '240ms' }} />
                        </div>
                        <span className="font-body text-[11px] text-ink/20">Querying 2.5 million data points...</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Input bar — conversation state */}
          <div className="px-4 pb-4 pt-2">
            <div className="max-w-[760px] mx-auto bg-white border border-ink/[0.08] rounded-xl shadow-[0_2px_20px_rgba(0,0,0,0.04)] overflow-hidden">
              <form onSubmit={e => { e.preventDefault(); sendMessage(input); }} className="flex items-center">
                <input
                  ref={inputRef}
                  data-testid="chat-input-active"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Follow up..."
                  className="flex-1 px-5 py-3.5 font-body text-sm text-ink bg-transparent outline-none placeholder:text-ink/25"
                  disabled={loading}
                />
                <button
                  data-testid="chat-send-active"
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="mr-2 w-9 h-9 rounded-lg bg-ink text-white flex items-center justify-center disabled:opacity-20 hover:bg-ink/80 transition-all"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
